import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import { queryOptions } from "@tanstack/react-query";
import { createClient } from "./supabase/admin";
import { PaymentMethodContext, PaymentMethods, Status } from "./supabase/enums";
import { createServerFn } from "@tanstack/react-start";
import { SalesChannels } from "./storefront/channel";

/** What the storefront can take: paid online when the order is placed. */
const STOREFRONT_PAYMENT_METHODS: PaymentMethods[] = [
  PaymentMethods.ideal,
  PaymentMethods.creditcard,
  PaymentMethods.applepay,
];

export const paymentMethodsQueryOptions = () =>
  queryOptions({
    queryKey: ["payment_methods"],
    queryFn: () => fetchPaymentMethods(),
  });

export const fetchPaymentMethods = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId, channel } }) => {
    const supabase = createClient();
    const isStorefront = channel === SalesChannels.storefront;

    const { data, error } = await supabase
      .from("payment_method_on_user")
      .select(
        "id, context, fee, fee_percentage, position, status, paymentMethod:payment_method_new!inner(type)"
      )
      .eq("payment_method_new.status", Status.active)
      .in(
        "context",
        isStorefront
          ? [PaymentMethodContext.webshop]
          : [PaymentMethodContext.delivery, PaymentMethodContext.pickup]
      )
      .eq("status", Status.active)
      .eq("user_id", userId)
      // The order the merchant set in the POS payment method settings.
      .order("position", { ascending: true });

    if (error) {
      console.error("Could not fetch payment methods", error);
      throw new Error("Could not fetch payment methods");
    }

    const offered = isStorefront
      ? data.filter((paymentMethodAvailability) =>
          STOREFRONT_PAYMENT_METHODS.includes(
            paymentMethodAvailability.paymentMethod.type as PaymentMethods
          )
        )
      : data;

    return offered.map((paymentMethodAvailability) => ({
      id: paymentMethodAvailability.id,
      type: paymentMethodAvailability.paymentMethod.type as PaymentMethods,
      context: paymentMethodAvailability.context as PaymentMethodContext,
      fee: paymentMethodAvailability.fee
        ? paymentMethodAvailability.fee / 100
        : 0,
      // Stored in hundredths of a percent, handed to the checkout as the
      // percentage a guest reads: 250 becomes 2.5 (%).
      feePercentage: paymentMethodAvailability.fee_percentage
        ? paymentMethodAvailability.fee_percentage / 100
        : 0,
      position: paymentMethodAvailability.position,
    }));
  });

export type PaymentMethod = Awaited<
  ReturnType<typeof fetchPaymentMethods>
>[number];
